import {useTriggerGeneration} from '@features/ai/streaming/useGeneration';
import amplitudeService from '@features/analytics/amplitudeService';
import {useSubscription} from '@features/subscription/subscriptionHook';
import {usePaywallModal} from '@features/subscription/PaywallModal';
import {buyCreditsOpenAtom} from '@features/subscription/buyCreditsAtom';
import {notifyGenerationBlocked} from '@features/subscription/notifyGenerationBlocked';
import {trpc} from '@features/utils/trpc';
import {useSetAtom} from 'jotai';
import {setStreamingGenTypeAtom} from '@features/ai/streaming/streamingGenTypeAtom';
import type {GenType} from '@features/flow/flowService';
import type {MessageAttachment} from '@features/db/messageAttachment';
import {captureException} from '@sentry/react';
import {useContext} from 'react';
import {FlowContext} from '@features/contexts/FlowContext';

type SendMessageInput = {
  screenId: string;
  messageText: string;
  attachments: MessageAttachment[];
  /** Explicit genType override — skips LLM detection and uses this directly */
  genType?: GenType;
  /** Required when genType is provided — used to update flow.genType as "latest used" default */
  flowId?: string;
};

type DetectionContext = {
  flowId: string;
  currentGenType: GenType;
};

export function useSendMessage() {
  const {effectiveSubscription} = useSubscription();
  const {openPaywallModal, openPastDueModal} = usePaywallModal();
  const canGenerate = effectiveSubscription?.canGenerate ?? false;
  const triggerGeneration = useTriggerGeneration();
  const setStreamingGenType = useSetAtom(setStreamingGenTypeAtom);
  const setBuyCreditsOpen = useSetAtom(buyCreditsOpenAtom);
  const flow = useContext(FlowContext).flow;
  const isComponentsFlow = flow.genType === 'components';

  const trpcUtils = trpc.useUtils();
  const detectGenTypeMutation = trpc.generation.detectGenType.useMutation();
  const updateFlowMutation = trpc.flow.updateFlow.useMutation();

  const mutation = trpc.generation.pushChatMessage.useMutation({
    onSuccess({flowId, screenId}) {
      if (!canGenerate) {
        notifyGenerationBlocked(
          effectiveSubscription,
          () => openPaywallModal('generation-limit'),
          () => setBuyCreditsOpen(true),
          () => openPastDueModal('generation-limit'),
        );
        return;
      }

      amplitudeService.logEvent('[Chat] send message', {
        flowId,
        screenId,
      });

      triggerGeneration(flowId, screenId);

      void trpcUtils.flow.getFlowReferenceImages.invalidate({flowId});
    },
  });

  /**
   * Send a message with optional genType detection.
   *
   * @param input - Message data (screenId, messageText, attachments)
   * @param detectionContext - If provided, runs LLM detection before sending.
   *   - flowId: Used to update flow.genType if detection result differs
   *   - currentGenType: Current platform context for detection
   */
  const sendMessage = async (
    input: SendMessageInput,
    detectionContext?: DetectionContext,
  ) => {
    // We don't need to do gentype detection for components
    if (isComponentsFlow) {
      return await mutation.mutateAsync(input);
    }

    let detectedGenType: GenType | undefined;

    // If an explicit genType was provided, use it directly (e.g., screen type conversion)
    if (input.genType) {
      detectedGenType = input.genType;
      setStreamingGenType({
        screenId: input.screenId,
        genType: detectedGenType,
      });

      // Update flow.genType as "latest used" default for new screens
      if (input.flowId) {
        await updateFlowMutation.mutateAsync({
          flowId: input.flowId,
          updates: {genType: detectedGenType},
        });
      }

      return mutation.mutateAsync({
        ...input,
        genType: detectedGenType,
      });
    }

    // Check if there's a previousScreen attachment with sourceGenType
    const previousScreenAttachment = input.attachments.find(
      (a): a is MessageAttachment & {type: 'previousScreen'} =>
        a.type === 'previousScreen',
    );

    if (previousScreenAttachment?.sourceGenType) {
      const sourceGenType = previousScreenAttachment.sourceGenType;
      const hasUserComment = input.messageText.trim().length > 0;

      if (hasUserComment) {
        // User provided a comment - run LLM detection to check for platform switch keywords
        // (e.g., "convert to desktop", "make this mobile")
        try {
          const result = await detectGenTypeMutation.mutateAsync({
            prompt: input.messageText,
            currentGenType: sourceGenType,
          });
          detectedGenType = result.genType;

          // Track platform switch from Generate Next Screen flow
          if (detectedGenType !== sourceGenType) {
            amplitudeService.logEvent(
              '[GenType] platform switch from next screen',
              {
                from: sourceGenType,
                to: detectedGenType,
                flowId: detectionContext?.flowId,
                screenId: input.screenId,
              },
            );
          }
        } catch (e) {
          captureException(e);
          console.warn(
            'GenType detection failed for next screen, using source genType',
          );
          // Fall back to source genType if detection fails
          detectedGenType = sourceGenType;
        }
      } else {
        // No comment - use source screen's platform directly
        detectedGenType = sourceGenType;
      }

      setStreamingGenType({
        screenId: input.screenId,
        genType: detectedGenType,
      });
    }
    // Run genType detection for regular canvas generation
    else if (detectionContext && input.messageText.trim()) {
      try {
        const result = await detectGenTypeMutation.mutateAsync({
          prompt: input.messageText,
          currentGenType: detectionContext.currentGenType,
        });
        detectedGenType = result.genType;

        // Update flow.genType if it changed (keeps "latest used" as default)
        if (detectedGenType !== detectionContext.currentGenType) {
          amplitudeService.logEvent('[GenType] platform switch detected', {
            from: detectionContext.currentGenType,
            to: detectedGenType,
            flowId: detectionContext.flowId,
            screenId: input.screenId,
          });

          await updateFlowMutation.mutateAsync({
            flowId: detectionContext.flowId,
            updates: {genType: detectedGenType},
          });
        }
      } catch (e) {
        captureException(e);
        console.warn('GenType detection failed, using current genType');
        // Fall back to current genType if detection fails
        detectedGenType = detectionContext.currentGenType;
      }

      // Set streaming atom so FrameNode renders with correct dimensions
      setStreamingGenType({
        screenId: input.screenId,
        genType: detectedGenType,
      });
    }

    // Send message with detected genType (or undefined if no detection)
    return mutation.mutateAsync({
      ...input,
      genType: detectedGenType,
    });
  };

  return {
    sendMessage,
  };
}
